import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Timer } from 'lucide-react';
import {
  getStoredCredentials,
  setStoredCredentials,
  getAccessTokenExpiresIn,
} from '@/utils/secureAuthStorage';
import { refreshToken, clearAuthStorage } from '@/services/keycloakAuthService';
import { SessionWarningBody } from './SessionWarningBody';

/**
 * Reverse session countdown displayed in the app header + persistent
 * warning toast that fires when the access token has 5 minutes or less
 * remaining.
 *
 * Source of truth: `access_token_expires_at`. The access token is what
 * actually authorizes API calls — once it lapses, requests fail with 401.
 * The refresh token has a longer horizon and gets used by the renew flow,
 * not displayed.
 *
 * UI + behaviour cloned from postexchange-webapp / domainboost_admin so
 * all three apps render an identical session experience. The only
 * differences are which auth-storage / refresh APIs each app exposes.
 */
const WARNING_THRESHOLD = 300; // 5 minutes — chip turns amber, toast appears
const TOAST_ID = 'session-expiry-warning';

// TEST MODE — when true, forces the warning toast + critical-style chip to
// appear from the start of every session so the UI / renew flow can be
// exercised (and visually diffed against the sibling apps) without waiting
// for the access token to age out. Production: false — toast appears only
// when ≤ WARNING_THRESHOLD seconds remain.
const TEST_MODE_WARN_AT_LOGIN = false;

const formatRemaining = (seconds: number): string => {
  if (seconds <= 0) return '00:00';
  const days = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (days > 0) return `${days}d ${pad(h)}h`;
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
};

const isAuthGuardEnabled = () =>
  (import.meta.env.VITE_AUTH_GUARD ?? 'true').toString().toLowerCase() !== 'false';

export function SessionTimer() {
  const [remaining, setRemaining] = useState<number>(() => Math.max(0, getAccessTokenExpiresIn()));
  const warningShownRef = useRef(false);
  const enforcedRef = useRef(false);
  const refreshingRef = useRef(false);
  const [, setRefreshTick] = useState(0);

  /** "Stay signed in" handler. Hits the auth-service refresh endpoint and
   *  persists the rotated credentials. The next tick picks up the new
   *  expiry automatically. */
  const handleReauth = useCallback(async () => {
    if (refreshingRef.current) return;
    const credentials = getStoredCredentials();
    if (!credentials?.refresh_token) return;
    refreshingRef.current = true;
    setRefreshTick((n) => n + 1);
    try {
      const response = await refreshToken(credentials.refresh_token);

      // The auth service may return the new tokens either nested under
      // `data.credentials` or directly under `data` (Keycloak-style).
      // Accept either — pick whichever has an access_token.
      const responseData = response?.data as Record<string, unknown> | undefined;
      const candidateNested = (responseData?.['credentials'] ?? null) as Record<
        string,
        unknown
      > | null;
      const candidateFlat =
        responseData && typeof responseData['access_token'] === 'string' ? responseData : null;
      const fresh = candidateNested ?? candidateFlat;

      if (fresh && typeof fresh['access_token'] === 'string') {
        const now = Math.floor(Date.now() / 1000);
        const expiresIn = Number(fresh['expires_in']) || 0;
        const refreshExpiresIn = Number(fresh['refresh_expires_in']) || 0;
        const tokenType = fresh['token_type'];
        const sessionState = fresh['session_state'];
        const scope = fresh['scope'];
        await setStoredCredentials({
          access_token: String(fresh['access_token']),
          refresh_token: String(fresh['refresh_token'] ?? credentials.refresh_token),
          expires_in: expiresIn,
          refresh_expires_in: refreshExpiresIn,
          token_type: typeof tokenType === 'string' ? tokenType : 'Bearer',
          session_state: typeof sessionState === 'string' ? sessionState : '',
          scope: typeof scope === 'string' ? scope : 'openid email profile',
          access_token_expires_at: now + expiresIn,
          refresh_token_expires_at: now + refreshExpiresIn,
        });
        toast.success('Session renewed.', {
          position: 'top-right',
          autoClose: 2500,
          toastId: 'session-renewed',
        });
        warningShownRef.current = false;
        toast.dismiss(TOAST_ID);
      } else {
        toast.error('Could not renew session. Please log in again.', {
          position: 'top-right',
          autoClose: 4000,
        });
      }
    } catch (err) {
      const axiosErr = err as {
        response?: {
          data?: { message?: string; error?: string };
          status?: number;
        };
        message?: string;
      };
      const serverMsg = axiosErr?.response?.data?.message ?? axiosErr?.response?.data?.error;
      const status = axiosErr?.response?.status;
      const msg = serverMsg
        ? `Could not renew session: ${serverMsg}${status ? ` (HTTP ${status})` : ''}`
        : (axiosErr?.message ?? 'Could not renew session. Please log in again.');
      toast.error(msg, { position: 'top-right', autoClose: 6000 });
    } finally {
      // Refs aren't subject to the React state-batching race that
      // require-atomic-updates guards against — this is a simple
      // mutex-style flag.
      // eslint-disable-next-line require-atomic-updates
      refreshingRef.current = false;
      setRefreshTick((n) => n + 1);
    }
  }, []);

  /** Fires when the access token has actually expired and the user is on
   *  a protected page. Force-clears storage + redirects to /login. */
  const enforceLogout = useCallback(() => {
    if (enforcedRef.current) return;
    enforcedRef.current = true;
    clearAuthStorage();
    toast.error('Your session has expired. Please log in again.', {
      position: 'top-right',
      autoClose: 4000,
      toastId: 'session-expired',
    });
    setTimeout(() => {
      const here = window.location.pathname;
      if (here !== '/login' && here !== '/register' && here !== '/forgot-password') {
        const from = here && here !== '/' ? `?from=${encodeURIComponent(here)}` : '';
        window.location.href = `/login${from}`;
      }
    }, 50);
  }, []);

  useEffect(() => {
    const tick = () => {
      const credentials = getStoredCredentials();
      if (!credentials) {
        setRemaining(0);
        if (warningShownRef.current) {
          warningShownRef.current = false;
          toast.dismiss(TOAST_ID);
        }
        return;
      }

      const realExpiresIn = Math.max(0, getAccessTokenExpiresIn());
      setRemaining(realExpiresIn);

      // The countdown chip renders whenever a session exists (above). The
      // enforcement below — force-logout + renewal toast — only runs when
      // the auth guard is on; in dev-bypass mode (VITE_AUTH_GUARD=false)
      // the chip still shows but we never force a logout. Mirrors PE,
      // which splits SessionCountdown (display) from useSessionWarning
      // (enforcement).
      if (!isAuthGuardEnabled()) {
        return;
      }

      // Token has expired — forcibly log the user out. Suppressed in TEST
      // MODE so the demo can keep showing the toast without redirecting.
      if (!TEST_MODE_WARN_AT_LOGIN && realExpiresIn <= 0) {
        enforceLogout();
        return;
      }

      // TEST MODE: open the warning on first tick of any active session.
      // Production threshold is WARNING_THRESHOLD seconds. Either way the
      // body displays the real remaining time so chip + toast always agree.
      const inWarningWindow = TEST_MODE_WARN_AT_LOGIN ? true : realExpiresIn <= WARNING_THRESHOLD;

      if (inWarningWindow) {
        const body = (
          <SessionWarningBody
            totalSeconds={realExpiresIn}
            isRefreshing={refreshingRef.current}
            onReauth={() => {
              void handleReauth();
            }}
          />
        );

        if (!warningShownRef.current) {
          warningShownRef.current = true;
          toast.warning(body, {
            autoClose: false,
            closeOnClick: false,
            draggable: false,
            icon: false,
            toastId: TOAST_ID,
            className: 'session-expiry-toast',
          });
        } else if (toast.isActive(TOAST_ID)) {
          toast.update(TOAST_ID, { render: body });
        } else {
          // Toast was manually dismissed — let it re-show next tick.
          warningShownRef.current = false;
        }
      }

      // Reset warning flag if session was refreshed (expires_in went back
      // above threshold). Skipped in TEST MODE so the toast keeps showing.
      if (!TEST_MODE_WARN_AT_LOGIN && realExpiresIn > WARNING_THRESHOLD) {
        warningShownRef.current = false;
        toast.dismiss(TOAST_ID);
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [enforceLogout, handleReauth]);

  if (remaining <= 0) return null;

  const expiresAt = new Date(Date.now() + remaining * 1000);
  // Chip styling matches `src/demo/chrome/DemoChromeA.tsx` exactly —
  // absolute-px sizing, rounded-full pill, Tailwind utility classes with
  // hex colors verbatim. Critical (amber) state overrides the calm
  // slate palette when the access token is within WARNING_THRESHOLD.
  const isCritical = TEST_MODE_WARN_AT_LOGIN || remaining <= WARNING_THRESHOLD;

  const chipClassName = [
    'hidden items-center gap-[6px] rounded-full px-[10px] py-[4px] sm:inline-flex',
    isCritical
      ? 'border border-[#FDE68A] bg-[#FEF9E7] text-[#A16207]'
      : 'border border-[#E2E8F0] bg-[#F8FAFC] text-[#64748B]',
  ].join(' ');

  return (
    <span
      className={chipClassName}
      title={`Session expires at ${expiresAt.toLocaleString()}`}
      data-testid="session-countdown"
      aria-label="Session time remaining"
    >
      <Timer className="h-[14px] w-[14px]" aria-hidden="true" />
      <span className="text-[10px] font-semibold uppercase tracking-wider">Session</span>
      <span className="text-[11px] font-[family-name:var(--font-mono)] font-medium tabular-nums">
        {formatRemaining(remaining)}
      </span>
    </span>
  );
}
